import { QueryLoader } from "@Light/components/app/QueryLoader";
import { useLight } from "@Light/services/light";
import { zip5 } from "@Light/utils/address";
import { useEffect, useRef } from "react";
import { useEnrollment } from "../flow/context";
import { EnrollRoute } from "../routes";
import { isEligible } from "./isEligible";
import { needsUtilityConfirmation } from "./needsUtilityConfirmation";

/**
 * Entry point of the zip-first flow, routing to whichever zip screen suits what
 * we already know, so those screens each handle only their own step.
 */
export const ZipUtility = () => {
  const { requirements, navigate } = useEnrollment();
  const { useCheckEligibilityQuery } = useLight();

  const existing = requirements.service_address?.value?.postal_code;
  const postalCode = existing ? zip5(existing) : undefined;
  const query = useCheckEligibilityQuery(
    { postal_code: postalCode ?? "" },
    { skip: !postalCode },
  );

  // Routing goes through the flow rather than react-router, so it happens in an
  // effect instead of by returning a redirect. It has to happen exactly once —
  // a second pass would fight the navigation that's already underway.
  const routed = useRef(false);
  useEffect(() => {
    if (routed.current) return;
    const to = (path: string) => {
      routed.current = true;
      // Replaces the history entry, since there's nothing here to come back to,
      // but stays in the flow's stack so advancing doesn't route back into it.
      navigate(path, { replace: true, keepInStack: true });
    };

    if (!postalCode || query.isError) {
      to(EnrollRoute.ZipUtilitySearch);
      return;
    }

    const result = query.data;
    if (!result) return;

    if (!isEligible(result)) {
      to(EnrollRoute.ZipUtilityIneligible);
    } else if (needsUtilityConfirmation(result)) {
      to(EnrollRoute.ZipUtilitySplit);
    } else {
      to(EnrollRoute.ZipUtilitySearch);
    }
  });

  return <QueryLoader queries={[{ isLoading: true }]} fullHeight />;
};
